import {Observable, of, Subject, throwError} from 'rxjs';
import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {catchError, filter, map, mergeMap, tap, takeUntil} from 'rxjs/operators';
import {select, Store} from '@ngrx/store';

import {environment} from '@env/environment';
import {AuthenticatedUserService} from '@app/core/service/authenticated-user.service';
import {User} from '@app/core/model/user';
import {Application} from '../../application-settings/models/application.model';
import {SyncHistoryItem} from '../../components/sync-history-timeline/models/sync-history-item.model';
import {DataPartitionStatistic, DataStatisticDirection} from '../../components/sync-history-timeline/models/data-partition-statistic.model';
import {ShortcutItem} from '../../components/sync-history-timeline/models/shortcut-item.model';
import {ShortcutOperation} from '../../components/sync-history-timeline/models/shortcut-operation.model';
import {Integration, INTEGRATION_TASKS_ORDER} from '../../application-settings/models/integration.model';
import {IntegrationTask} from '../../application-settings/models/integration-task.model';
import {DataSourceType} from '../../application-settings/models/data-source-type.model';
import {DataAttribute, GroupedDataAttribute} from '../../application-settings/models/data-attribute.model';
import {InvalidEntityModel} from '../../application-settings/models/invalid-entity.model';
import {ApplicationProviderListModel} from '../../application-settings/models/application-provider-list.model';
import {IntegrationMode} from '../../application-settings/models/integration-mode.model';
import {HistoryErrorType} from '../../application-settings/models/history-error-type';
import {
    RequestResponseAuditInfo,
    RESPONSE_STATUS_TEXT,
    TransientEtxMessageAuditInfo
} from '../../components/sync-history-timeline/models/audit-info.model';
import {
    AggregatedValidationData,
    AggregatedValidationInfoModel,
    AggregatedValidationStatsByRosterType,
    GroupedAggregatedValidationInfo,
    GroupedAggregatedValidationInfoByIntegrationMode
} from '../../application-settings/models/aggregated-validation-info.model';
import {
    ValidationRuleWithStatByIntegrationMode,
    GroupedValidationRuleWithStat,
    ValidationRuleWithStatModel
} from '../../application-settings/models/validation-rule-with-stat.model';
import {
    DuplicateModel, DuplicatesMetadata,
    DuplicatesMetadataConfig,
    DuplicatesSearchConfig,
    SourceEntityModel
} from '../../application-settings/models/duplicates.model';
import {AttributeMappingDirection, IntegrationTaskMap} from '../../application-settings/models/integration-task-map.model';
import {OrgItem} from '../../application-settings/models/orgItem.model';
import {ProcessHistoryState} from '@app/views/admin/components/sync-history-timeline/models/process-history-state.model';
import {isAuthenticated, State} from '@app/app.reducers';
import {FilterPredicate} from '@app/core/model/filtering/filter-predicate';
import {ExtraAttribute, FilterAction, TASK_EXTRA_ATTRIBUTES} from '@app/core/model/filtering/entity-filter-model';
import {SignificantChangesReport} from '@app/views/admin/application-settings/models/significant-changes.model';
import {IPiiFreeModel} from '@app/core/comp/filtering/filter';
import {
    ETXTransactionHistory,
    ETXTransactionHistoryProcessingDetails,
    ETXTransactionHistoryItemContent,
    ETXTransactionHistoryReceivingDetails,
    TransactionHistoryItemConnect,
    ETXProcessingRelaunchResponse,
    TransactionHistoryItemStatuses,
    ETXTransactionHistoryStatuses
} from '@app/views/admin/components/transactions-history/transactions-history-timeline/models/transaction-history-item.model';
import {TransactionMessageItem} from '@app/views/admin/components/transactions-history/transactions-history-timeline/models/transaction-message-item.model';
import {DistrictWithSchools} from '@app/views/admin/components/sync-history-timeline/models/district-with-schools';
import {ADAWIntegration, ADAWLogEvent, ADAWSchedule} from '@app/views/admin/application-settings/models/adaw-application.model';
import {ProcessConfig} from '@app/views/admin/application-settings/models/process-config.model';
import {ApplicationSettingsService} from '@app/views/admin/application-settings/application-settings.service';
import {ProcessRunMode} from '@app/views/admin/application-settings/models/process-run-mode.model';
import {
    OnboardingWizardStep, OnboardingWizardStepNames,
    ProviderOnboardingWizard
} from '@app/views/admin/components/onboarding-wizard/onboarding-wizard.model';

@Injectable()
export class ApplicationsService {

    public readonly DEFAULT_HISTORY_LIMIT = 20;
    public readonly DEFAULT_TRANSACTION_HISTORY_LIMIT = 20;
    public readonly FILTERED_TRANSACTION_HISTORY_LIMIT = 100;
    public readonly DEFAULT_SHORTCUT_LIMIT = 20;

    private readonly PREDICATE_TITLES = {
        [FilterPredicate.IS_EMPTY]: 'Value is missing',
        [FilterPredicate.IS_NOT_VALID_EMAIL]: 'Email address is invalid',
        [FilterPredicate.IS_STUDENTS_EXIST]: 'Students exist',
        [FilterPredicate.IS_STUDENTS_NOT_EXIST]: 'Students are missing',
        [FilterPredicate.IS_ROSTERS_EMPTY]: 'Classes are missing'
    };

    private readonly PREDICATE_TITLES_SHORT = {
        [FilterPredicate.IS_EMPTY]: 'is missing',
        [FilterPredicate.IS_NOT_VALID_EMAIL]: 'is invalid',
        [FilterPredicate.IS_STUDENTS_EXIST]: 'exist',
        [FilterPredicate.IS_STUDENTS_NOT_EXIST]: 'are missing',
        [FilterPredicate.IS_ROSTERS_EMPTY]: 'are missing'
    };

    private searchResultAuditLog = new Subject<{ step: number; control: string }>();
    public searchCounterAuditLog = new Subject<{ selectedCounter: number; totalCounter: number; control: string }>();
    private itemAuditLogSearch = new Subject<{ step: number; control: string }>();

    searchResultAuditLog$ = this.searchResultAuditLog.asObservable();
    searchCounterAuditLog$ = this.searchCounterAuditLog.asObservable();
    itemAuditLogSearch$ = this.itemAuditLogSearch.asObservable();

    static getResponseStatusColor(responseStatus: any, responseStatusText: string) {
        switch (true) {
            case ((responseStatus >= 200 && responseStatus < 300)
                || (responseStatusText == RESPONSE_STATUS_TEXT.SUCCESS)
                || (responseStatusText == RESPONSE_STATUS_TEXT.OK)):
                return 'successful';

            case ((responseStatus >= 400) || (responseStatusText == RESPONSE_STATUS_TEXT.FAILURE)):
                return 'error';

            case (responseStatus < 200):
                return 'informational';

            case (responseStatus >= 300 && responseStatus < 400):
                return 'redirection';

        }
    }

    static convertSqsMessageToJson(sqsMessage: string | any): Object {
        let data;
        const result = {};
        try {
            data = JSON.parse(sqsMessage);
        } catch {
            // ToDo: Fix this it seems this is not working - probably the format was changed
            let str = sqsMessage;
            str = str.replace(/\\"/g, '\\\\"');
            str = str.replace(/"/g, '\\\"');
            str = str.replace(/=\[/g, '":"');
            str = str.replace(/\], /g, '", "');
            str = str.replace(/{/, '\{"');
            str = str.replace(/\]\}$/, '"\}');
            data = JSON.parse(str);
        }

        const JSONdataKeys = ['entitySnapshot', 'orgsMappings'];
        Object.keys(data).forEach((key: string) => {
            const propertyNamesChain = key.split('.');
            propertyNamesChain.reduce(
                (obj: Object, propertyName: string, index: number) => {
                    if (index === (propertyNamesChain.length - 1)) {
                        obj[propertyName] = data[key];
                        if ((propertyName === 'MessageBody') || (propertyName.substring(0, 1) === '{')) {
                            try {
                                obj[propertyName] = JSON.parse(obj[propertyName], (k, v) => {
                                    const vIsJSONString = (typeof v === 'string' || v instanceof String) && ((v.substring(0, 1) === '{') || (v.substring(0, 2) === '[{'));
                                    if ((JSONdataKeys.indexOf(k) > -1) || vIsJSONString) {
                                        try {
                                            return JSON.parse(v);
                                        } catch (e) {
                                            // console.log(e);
                                        }
                                    }
                                    return v;
                                });
                            } catch (e) {
                                // console.log(e);
                            }
                        }
                        return obj;
                    } else {
                        if (!obj[propertyName]) {
                            obj[propertyName] = {};
                        }
                        return obj[propertyName];
                    }
                },
                result
            );
        });
        return result;
    }

    static convertADAWIntegrationToApplicationProviderListModel(app: ADAWIntegration): ADAWIntegration {
        const adawIntegration = new ADAWIntegration(
            app.name,
            app.id,
            app.id,
            IntegrationMode.ADAW,
            app?.logoUrl === 'http://logo.url.test' ? 'assets/images/logos/angular.png' : app?.logoUrl, // ToDo: change this, remove ternary operator
            app?.lastRun?.timestamp || null,
            app?.lastRun?.success ? ProcessHistoryState.OK : ProcessHistoryState.FAILED,
        );

        const processConfigModel = [];
        (app.schedules || []).forEach((schedule: ADAWSchedule) => {
            schedule.cronExpression.forEach((cronExpression: string) => {
                const processConfigItem = new ProcessConfig(
                    false,
                    schedule?.id,
                    cronExpression,
                    ProcessRunMode.SCHEDULED

                );
                processConfigModel.push(processConfigItem);
            });
        });
        app.processConfigModel = ApplicationSettingsService.getExtendedProcessConfig(processConfigModel);
        app.nextSyncDateList = ApplicationSettingsService.getNextSyncDateList(processConfigModel, app);
        app.nextExecutionDate = app?.nextSyncDateList[0]?.toString();
        app.validationErrorsCount = 0; // ToDo: change this !!!
        return Object.assign(adawIntegration, app);
    }

    constructor(private http: HttpClient,
                private store: Store<State>,
                private authenticatedUserService: AuthenticatedUserService) {
    }

    getApplications(orgGuid: string): Observable<ApplicationProviderListModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/provider/${orgGuid}`;

        return this.http.get<ApplicationProviderListModel[]>(url)
            .pipe(
                map((applications: ApplicationProviderListModel[]) => {
                    applications.sort((app1: ApplicationProviderListModel, app2: ApplicationProviderListModel) => app1.name.localeCompare(app2.name));
                    return applications;
                }),
                catchError(this.handleError),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    getADAWIntegrations(orgGuid: string): Observable<ADAWIntegration[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/adaw/districts/${orgGuid}/applications`;

        return this.http.get<ADAWIntegration[]>(url)
            .pipe(
                map((applications: ADAWIntegration[]) => {
                    applications.sort((app1: ADAWIntegration, app2: ADAWIntegration) => app1.name.localeCompare(app2.name));
                    return applications.map((app: ADAWIntegration) => ApplicationsService.convertADAWIntegrationToApplicationProviderListModel(app));
                }),
                catchError(this.handleError),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    getADAWIntegration(orgGuid: string, appId: string): Observable<ADAWIntegration> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/adaw/districts/${orgGuid}/applications/${appId}`;

        return this.http.get<ADAWIntegration>(url)
            .pipe(
                map((application: ADAWIntegration) => {
                    return ApplicationsService.convertADAWIntegrationToApplicationProviderListModel(application);
                }),
                catchError(this.handleError),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    getADAWApplicationLogs(orgGuid: string, appId: string, lastId: string = '', filterValue: string = '', limit: number = 100): Observable<ADAWLogEvent[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/adaw/districts/${orgGuid}/applications/${appId}/logs`;

        const options = {
            params: {
                'fromId': lastId,
                'limit': limit
            }
        };
        return this.http.get<ADAWLogEvent[]>(url, options)
            .pipe(
                catchError(this.handleError),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    runADAWSync(orgGuid: string, appId: string): any {
        const url = `${environment.datahubBaseURI}/api/v2/applications/adaw/districts/${orgGuid}/applications/${appId}/start`;

        return this.http.post(url, '', {responseType: 'text' as const})
            .pipe(catchError(this.handleError));
    }

    checkADAWAppStatus(orgGuid: string, appId: string): any {
        const url = `${environment.datahubBaseURI}/api/v2/applications/adaw/districts/${orgGuid}/applications/${appId}/status`;

        return this.http.get(url)
            .pipe(catchError(this.handleError));
    }

    public getPiiFreeApplications(orgGuid: string): Observable<IPiiFreeModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/pii/orgs/${orgGuid}/pii_free`;

        return this.http
            .get<IPiiFreeModel[]>(url)
            .pipe(
                catchError(this.handleError),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    getApplicationsHistory(lastId: string = null, limit: number = this.DEFAULT_HISTORY_LIMIT, appGUIDList: string[] = []): Observable<SyncHistoryItem[]> {

        // Implement get district history for sysadm
        if (this.authenticatedUserService.isSysAdmin()) {
            return of([]);
        }

        const appList = appGUIDList.join(',');
        let url = `${environment.datahubBaseURI}/api/v2/applications/integrations/stats`
            + `?rowCount=${limit}&applications=${appList}`;
        if (lastId) {
            url += `&lastId=${lastId}`;
        }

        return this.http.get<SyncHistoryItem[]>(url)
            .pipe(catchError(this.handleError));
    }

    getIntegrationHistory(integrationId: string, lastId: string = null, limit: number = this.DEFAULT_HISTORY_LIMIT): Observable<SyncHistoryItem[]> {

        let url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/process/stats`
            + `?rowCount=${limit}`;
        if (lastId) {
            url += `&lastId=${lastId}`;
        }

        return this.http.get<SyncHistoryItem[]>(url)
            .pipe(catchError(this.handleError));
    }

    getApplicationHistoryProcessing(applications: Application[], institutionGuid: string) {
        return ([applicationsHistory, significantChangesReportsMap]: [SyncHistoryItem[], Map<string, SignificantChangesReport>]) => {
            applicationsHistory.sort((item1, item2) => new Date(item2.startTime).getTime() - new Date(item1.startTime).getTime());

            // Remove the last item if it has No Changes status - because we don't have the stats data for it
            //  (only for App / District / School history)
            if (applications.length === 1) {
                if ((applicationsHistory.length > 0)
                    && (applicationsHistory[applicationsHistory.length - 1].state === ProcessHistoryState.NO_CHANGES)) {
                    applicationsHistory.pop();
                }
            }

            const appsLastSessionMap = new Map<string, number[]>();
            for (let i = applicationsHistory.length - 1; i >= 0; i--) {
                const applicationHistoryItem = applicationsHistory[i];
                if (applicationHistoryItem.state === ProcessHistoryState.NO_CHANGES) {
                    if (appsLastSessionMap.has(applicationHistoryItem.applicationId)) {
                        [applicationHistoryItem.prevSessionId, applicationHistoryItem.prevSessionStatId] =
                            appsLastSessionMap.get(applicationHistoryItem.applicationId);
                    }
                } else {
                    appsLastSessionMap.set(applicationHistoryItem.applicationId,
                        [applicationHistoryItem.sessionId, applicationHistoryItem.statId]);
                }
            }

            applicationsHistory = applicationsHistory.map((applicationHistoryItem: SyncHistoryItem) =>
                this.processHistoryItem(applicationHistoryItem, significantChangesReportsMap, applications, institutionGuid)
            );

            return applicationsHistory;
        };
    }

    processHistoryItem(applicationHistoryItem: SyncHistoryItem, significantChangesReportsMap: Map<string, SignificantChangesReport>, applications: Application[], institutionGuid: string): SyncHistoryItem {
        applicationHistoryItem.institutionGuid = institutionGuid;

        if (applicationHistoryItem.dataSourceType) {
            applicationHistoryItem.dataSourceType = applicationHistoryItem.dataSourceType.split('_')[0];
        }

        if (!applicationHistoryItem.appOwnerGuid || !applicationHistoryItem.applicationType) {
            const application = applications.find((app: Application) =>
                app.guid === applicationHistoryItem.applicationId
            );

            if (application) {
                if (!applicationHistoryItem.appOwnerGuid) {
                    applicationHistoryItem.appOwnerGuid = application.ownerId;
                }
                if (!applicationHistoryItem.applicationType) {
                    applicationHistoryItem.applicationType = application.type;
                }
            }
        }

        if (applicationHistoryItem.duplicates) {
            applicationHistoryItem.duplicates = applicationHistoryItem.duplicates.map((duplicate: SyncHistoryItem) =>
                this.processHistoryItem(duplicate, significantChangesReportsMap, applications, institutionGuid)
            );
        }

        if (applicationHistoryItem.sessionId && significantChangesReportsMap.has(applicationHistoryItem.sessionId.toString())) {
            applicationHistoryItem.significantChangeReport = significantChangesReportsMap.get(applicationHistoryItem.sessionId.toString());
        }

        return applicationHistoryItem;
    }

    getSharedPoolHistory(sharedPoolGuid: string, lastId: string = null, limit: number = this.DEFAULT_HISTORY_LIMIT): Observable<SyncHistoryItem[]> {

        let url = `${environment.datahubBaseURI}/api/v2/applications/process/shared_pool/${sharedPoolGuid}/stats`
            + `?rowCount=${limit}`;
        if (lastId) {
            url += `&lastId=${lastId}`;
        }

        return this.http.get<SyncHistoryItem[]>(url)
            .pipe(
                map((syncHistoryItems: SyncHistoryItem[]) =>
                    syncHistoryItems.map((syncHistoryItem: SyncHistoryItem) => {
                        syncHistoryItem.sharedPoolGuid = sharedPoolGuid;
                        return syncHistoryItem;
                    })
                ),
                catchError(this.handleError)
            );
    }

    getProcessStats(integrationId: string, sessionId: number): Observable<DataPartitionStatistic[]> {

        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/process/stats/${sessionId}`;

        return this.http.get<DataPartitionStatistic[]>(url)
            .pipe(
                map((statsData: DataPartitionStatistic[]) =>
                    statsData.filter((partitionStatistic: DataPartitionStatistic) =>
                        !DataPartitionStatistic.isEmpty(partitionStatistic)
                    )
                ),
                catchError(this.handleError)
            );
    }

    getSharedPoolProcessStats(sharedPoolGuid: string, sessionId: number): Observable<DataPartitionStatistic[]> {

        const url = `${environment.datahubBaseURI}/api/v2/applications/shared_pool/process/stats/${sessionId}`;

        return this.http.get<DataPartitionStatistic[]>(url)
            .pipe(
                map((statsData: DataPartitionStatistic[]) =>
                    statsData.filter((partitionStatistic: DataPartitionStatistic) =>
                        !DataPartitionStatistic.isEmpty(partitionStatistic)
                    )
                ),
                catchError(this.handleError)
            );
    }

    sumDataPartitionStatistic(summary: DataPartitionStatistic, processStatItem: DataPartitionStatistic): DataPartitionStatistic {
        summary.totalCount += processStatItem.totalCount || 0;
        summary.errorsCount += processStatItem.errorsCount || 0;

        summary.notChangedCount += processStatItem.notChangedCount || 0;
        summary.newCount += processStatItem.newCount || 0;
        summary.updatedCount += processStatItem.updatedCount || 0;
        summary.deletedCount += processStatItem.deletedCount || 0;
        summary.mergedCount += processStatItem.mergedCount || 0;
        summary.skippedCount += processStatItem.skippedCount || 0;
        summary.skippedByVendorCount += processStatItem.skippedByVendorCount || 0;
        summary.duplicatesCount += processStatItem.duplicatesCount || 0;
        summary.ignoredCount += processStatItem.ignoredCount || 0;
        summary.invalidCount += processStatItem.invalidCount || 0;

        return summary;
    }

    getApplicationHistoryShortcuts(syncHistoryItem: SyncHistoryItem, statId: number, rosterType: string,
        partition: string = null, direction: DataStatisticDirection = null,
        operation: ShortcutOperation | ShortcutOperation[] = null, lastGuid: string = null,
        filterStr: string = null, rowCount: number = this.DEFAULT_SHORTCUT_LIMIT): Observable<ShortcutItem[]> {

        const params = {
            operation: Array.isArray(operation) ? operation.join(',') : operation,
            partition: partition ? encodeURIComponent(partition) : partition,
            direction: direction,
            filter: filterStr ? encodeURIComponent(filterStr) : filterStr,
            lastGuid: lastGuid,
            rowCount: rowCount
        };

        Object.keys(params).forEach((paramName: string) => {
            if (params[paramName] === null) {
                delete params[paramName];
            }
        });

        let result$: Observable<ShortcutItem[]>;
        let url = `${environment.datahubBaseURI}/api/v2/applications/`;
        const suffix = `/executions/${statId}/shortcuts/${rosterType}`;

        if (syncHistoryItem.sharedPoolGuid) {
            url += `shared_pool/${syncHistoryItem.sharedPoolGuid}` + suffix;
            const options = {
                headers: {'Content-Type': 'multipart/form-data; boundary=WebAppBoundary'},
                params: params
            };
            result$ = this.http.post<ShortcutItem[]>(url, null, options);
        } else {
            url += `integrations/${syncHistoryItem.integrationId}` + suffix;
            result$ = this.http.get<ShortcutItem[]>(url, {params: params});
        }

        return result$.pipe(catchError(this.handleError));
    }

    getApplicationHistoryTotalShortcuts(syncHistoryItem: SyncHistoryItem, statId: number, rosterType: string,
        partition: string = null, direction: DataStatisticDirection = null, lastGuid: string = null,
        filterStr: string = null, rowCount: number = this.DEFAULT_SHORTCUT_LIMIT): Observable<ShortcutItem[]> {

        const params = {
            rosterType: rosterType,
            partition: partition ? encodeURIComponent(partition) : partition,
            direction: direction,
            filter: filterStr ? encodeURIComponent(filterStr) : filterStr,
            lastGuid: lastGuid,
            rowCount: rowCount
        };

        const args = Object.keys(params)
            .filter((paramName: string) => params[paramName] !== null)
            .map((paramName: string) => `${paramName}=${params[paramName]}`)
            .join('&');

        let url = `${environment.datahubBaseURI}/api/v2/applications/`;
        if (syncHistoryItem.sharedPoolGuid) {
            url += `shared_pool/${syncHistoryItem.sharedPoolGuid}`;
        } else {
            url += `integrations/${syncHistoryItem.integrationId}`;
        }
        url += `/executions/${statId}/shortcuts/total?${args}`;

        return this.http.get<ShortcutItem[]>(url)
            .pipe(catchError(this.handleError));
    }

    getApplicationHistoryErrorShortcuts(syncHistoryItem: SyncHistoryItem, statId: number, filterParams: Object = {},
        lastGuid: string = null, rowCount: number = this.DEFAULT_SHORTCUT_LIMIT): Observable<ShortcutItem[]> {

        const params = {
            ...filterParams,
            lastGuid: lastGuid,
            rowCount: rowCount
        };

        const args = Object.keys(params)
            .filter((paramName: string) => params[paramName] !== null)
            .map((paramName: string) => `${paramName}=${params[paramName]}`)
            .join('&');

        let url = `${environment.datahubBaseURI}/api/v2/applications/`;
        if (syncHistoryItem.sharedPoolGuid) {
            url += `shared_pool/${syncHistoryItem.sharedPoolGuid}`;
        } else {
            url += `integrations/${syncHistoryItem.integrationId}`;
        }
        url += `/executions/${statId}/shortcuts/errors?${args}`;

        return this.http.get<ShortcutItem[]>(url)
            .pipe(catchError(this.handleError));
    }

    getSharedPoolDistrictsWithSchools(sharedPoolGuid: string): Observable<DistrictWithSchools[]> {

        const url = `${environment.datahubBaseURI}/api/v2/applications/shared_pools/districts/${sharedPoolGuid}`;

        return this.http.get<DistrictWithSchools[]>(url)
            .pipe(catchError(this.handleError));
    }

    getApplicationHistorySupportDetails(
        integrationId: string,
        statId: number,
        search: string,
        level: string,
        limit: number,
        lastId: string,
        direction: string
    ): Observable<ShortcutItem[]> {

        const params = {
            search: encodeURIComponent(search),
            level: level,
            limit: limit,
            lastId: lastId,
            direction: direction
        };

        const args = Object.keys(params)
            .filter((paramName: string) => params[paramName] !== null)
            .map((paramName: string) => `${paramName}=${params[paramName]}`)
            .join('&');

        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/process/messages/${statId}?${args}`;

        return this.http.get<ShortcutItem[]>(url)
            .pipe(
                map((shortcutList: ShortcutItem[]) => shortcutList),
                catchError(this.handleError)
            );
    }

    // Convert 022-01-19T16:58:49.685+00:00 => 2022-01-19 16:58:49.685
    private getFormattedDateString(date: any): string {
        if (date instanceof Date) {
            const dateVal = String(date.getUTCDate()).padStart(2, '0');
            const monthVal = String(date.getUTCMonth() + 1).padStart(2, '0');
            return `${date.getUTCFullYear()}-${monthVal}-${dateVal} `
                + `${date.getUTCHours()}:${date.getUTCMinutes()}:${date.getUTCSeconds()}`;
        } else {
            return date.replace(/T/, ' ')
                .split('+')
                [0];
        }
    }

    getApplicationHistoryAuditInfo(integrationId: string, statId: number, sessionStartTime: any, sessionEndTime: any,
        requestUri: string, requestBody: string,
        responseBody: string, responseStatus: string, responseStatusText: string,
        lastId: string, rowCount: number): Observable<RequestResponseAuditInfo[]> {

        const params = {
            requestId: lastId,
            rowCount: rowCount,
            sessionStartTime: sessionStartTime ? this.getFormattedDateString(sessionStartTime) : '',
            sessionEndTime: sessionEndTime ? this.getFormattedDateString(sessionEndTime) : '',
            requestUri: encodeURIComponent(requestUri),
            requestBody: encodeURIComponent(requestBody),
            responseBody: encodeURIComponent(responseBody),
            responseStatus: encodeURIComponent(responseStatus),
            responseStatusText: encodeURIComponent(responseStatusText)
        };

        const args = Object.keys(params)
            .filter((paramName: string) => (params[paramName] != null && params[paramName] != ''))
            .map((paramName: string) => `${paramName}=${params[paramName]}`)
            .join('&');

        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/process/${statId}/audit?${args}`;

        return this.http.get<RequestResponseAuditInfo[]>(url)
            .pipe(catchError(this.handleError));
    }

    getApplicationEtxHistoryAuditInfo(integrationId: string, statId: number, messageBody: string, lastId: string, rowCount: number): Observable<TransientEtxMessageAuditInfo[]> {

        const params = {
            lastId: lastId,
            rowCount: rowCount,
            messageBody: encodeURIComponent(messageBody),
            ds: 'passport'
        };

        const args = Object.keys(params)
            .filter((paramName: string) => (params[paramName] != null && params[paramName] != ''))
            .map((paramName: string) => `${paramName}=${params[paramName]}`)
            .join('&');

        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/process/${statId}/audit/transient_msgs?${args}`;

        return this.http.get<TransientEtxMessageAuditInfo[]>(url)
            .pipe(catchError(this.handleError));
    }

    getTransactionsHistory(
        orgGuid: string, offset: number = 0, limit: number = this.DEFAULT_TRANSACTION_HISTORY_LIMIT, getFailedStatusesOnly?: boolean, domain?: string
    ): Observable<TransactionHistoryItemConnect[]> {

        let url = `${environment.datahubBaseURI}/api/v2/audit/sqs/transactions-history/orgs/${orgGuid}?limit=${limit}&offset=${offset}`;
        if (getFailedStatusesOnly) {
            url += `&status=${TransactionHistoryItemStatuses.FAILED}`;
        }
        if (domain) {
            url += `&domain=${domain}`;
        }

        return this.http.get<TransactionHistoryItemConnect[]>(url)
            .pipe(catchError(this.handleError));
    }

    getHistoryTransactionById(transactionId: string): Observable<TransactionHistoryItemConnect> {
        const url = `${environment.datahubBaseURI}/api/v2/audit/sqs/transactions-history/transactions/${transactionId}`;
        return this.http.get<TransactionHistoryItemConnect>(url)
            .pipe(catchError(this.handleError));
    }

    getTransactionsHistoryETX(
        orgGuid: string,
        lastId: string = null,
        limit: number = this.DEFAULT_TRANSACTION_HISTORY_LIMIT,
        getFailedStatusesOnly?: boolean,
        domain?: string
    ): Observable<TransactionHistoryItemConnect[]> {

        let url = `${environment.etxBaseURI}/sync/stat/session/list?district=${orgGuid}` + `&psize=${limit}`;
        // if (lastId) {
        //     url += `&lastId=${lastId}`;
        // }
        return this.http.get<TransactionHistoryItemConnect[]>(url)
            .pipe(catchError(this.handleError));
    }

    getTransactionsHistoryListETX(
        orgGuid?: string, transactionIdList?: string[], getFailedStatusesOnly?: boolean
    ): Observable<ETXTransactionHistoryItemContent[]> {

        const url = `${environment.etxBaseURI}/sync/processing/session/list`;

        const formData = new FormData();
        if (orgGuid) {
            formData.append('district', orgGuid);
        }
        if (getFailedStatusesOnly) {
            formData.append('status', ETXTransactionHistoryStatuses.FAILED);
        }
        if (transactionIdList && (transactionIdList.length > 0)) {
            formData.append('sessions', transactionIdList.join(','));
        }

        return this.http.post<ETXTransactionHistoryItemContent[]>(url, formData);
    }

    getFilteredTransactionsHistoryETX(
        orgGuid: string, pageOffset: number = 1, getFailedStatusesOnly?: boolean, limit: number = this.FILTERED_TRANSACTION_HISTORY_LIMIT
    ): Observable<ETXTransactionHistory> {

        const params = {
            district: orgGuid,
            pnum: pageOffset,
            psize: limit,
            status: getFailedStatusesOnly ? ETXTransactionHistoryStatuses.FAILED : ''
        };
        const args = Object.keys(params)
            .filter((paramName: string) => (params[paramName] != null && params[paramName] != ''))
            .map((paramName: string) => `${paramName}=${params[paramName]}`)
            .join('&');

        return this.http.get<ETXTransactionHistory>(`${environment.etxBaseURI}/sync/processing/session/pages/list?${args}`);
    }

    getETXTransactionHistoryReceivingDetails(transactionId: string): Observable<ETXTransactionHistoryReceivingDetails> {
        const url = `${environment.etxBaseURI}/sync/receiving/session/details?session_id=${transactionId}`;
        return this.http.get<ETXTransactionHistoryReceivingDetails>(url);
    }

    getETXTransactionHistoryProcessingDetails(transactionId: string): Observable<ETXTransactionHistoryProcessingDetails> {
        const url = `${environment.etxBaseURI}/sync/processing/session/details?session_id=${transactionId}`;
        return this.http.get<ETXTransactionHistoryProcessingDetails>(url);
    }

    restartETXProcessing(transactionId: string): Observable<ETXProcessingRelaunchResponse> {
        const url = `${environment.etxBaseURI}/sync/processing/relaunch`;

        const formData = new FormData();
        if (transactionId) {
            formData.append('session_id', transactionId);
        }

        return this.http.post<ETXProcessingRelaunchResponse>(url, formData)
            .pipe(catchError(this.handleError));
    }

    getTransactionMessages(
        transactionId: string, params: {orgGuid: string, entityGuid?: string, offset?: number, limit?: number, status?: string}
    ): Observable<TransactionMessageItem[]> {

        let args = Object.keys(params)
            .filter((paramName: string) => (params[paramName] != null && params[paramName] != ''))
            .map((paramName: string) => `${paramName}=${params[paramName]}`)
            .join('&');

        const url = `${environment.datahubBaseURI}/api/v2/audit/sqs/messages/${transactionId}?${args}`;

        return this.http.get<TransactionMessageItem[]>(url)
            .pipe(catchError(this.handleError));
    }

    processAuditInfo(auditData: RequestResponseAuditInfo[]): RequestResponseAuditInfo[] {
        auditData.forEach((auditInfoItem: RequestResponseAuditInfo) => {
            const pos = (auditInfoItem.requestUri || '').indexOf('?');
            auditInfoItem.requestUriShort = pos > 0 ? (auditInfoItem.requestUri || '').substring(0, pos) : null;
        });
        return auditData;
    }

    toggleSearchResultAuditLog(step: number, control: string) {
        this.searchResultAuditLog.next({step: step, control: control});
    }

    toggleSearchCounterAuditLog(selectedCounter: number, totalCounter: number, control: string) {
        this.searchCounterAuditLog.next({selectedCounter: selectedCounter, totalCounter: totalCounter, control: control});
    }

    toggleItemAuditLogSearch(step: number, control: string) {
        this.itemAuditLogSearch.next({step: step, control: control});
    }

    getAggregatedValidationInfoByIntegration(): Observable<GroupedAggregatedValidationInfo> {
        return this.getInvalidEntitiesStats()
            .pipe(map((data: AggregatedValidationInfoModel[]) => this.processAggregatedValidationInfoByIntegration(data)));
    }

    processAggregatedValidationInfoByIntegration(data: AggregatedValidationInfoModel[]): GroupedAggregatedValidationInfo {
        return this.groupByAndSumAggregatedValidationInfo(data, 'integrationId');
    }

    getAggregatedValidationInfoByRosterType(integrationId: string = null): Observable<GroupedAggregatedValidationInfoByIntegrationMode[]> {
        return this.getInvalidEntitiesStats(integrationId)
            .pipe(
                map((data: AggregatedValidationInfoModel[]) => {
                    const groupedStatsByIntegrationMode = this.groupBy(data, 'integrationMode');

                    const result = <GroupedAggregatedValidationInfoByIntegrationMode[]>[];
                    Object.keys(groupedStatsByIntegrationMode).forEach(key => {
                        result.push(new GroupedAggregatedValidationInfoByIntegrationMode(
                            <IntegrationMode>key,
                            this.groupByAndSumAggregatedValidationInfo(groupedStatsByIntegrationMode[key], 'rosterType')
                        ));
                    });
                    return result;
                })
            );
    }

    getAggregatedValidationStatsByRosterType(): Observable<AggregatedValidationStatsByRosterType[]> {
        return this.getInvalidEntitiesStats()
            .pipe(map((data: AggregatedValidationInfoModel[]) => this.processAggregatedValidationStatsByRosterType(data)));
    }

    processAggregatedValidationStatsByRosterType(data: AggregatedValidationInfoModel[]): AggregatedValidationStatsByRosterType[] {
        const groupedStatsByRosterType: GroupedAggregatedValidationInfo = this.groupByAndSumAggregatedValidationInfo(data, 'rosterType');

        const statsData: AggregatedValidationStatsByRosterType[] = Object.keys(groupedStatsByRosterType)
            .map((rosterType: string) =>
                new AggregatedValidationStatsByRosterType(rosterType, groupedStatsByRosterType[rosterType]));

        statsData.sort((item1: AggregatedValidationStatsByRosterType, item2: AggregatedValidationStatsByRosterType) =>
            (INTEGRATION_TASKS_ORDER[item1.rosterType] || 100) - (INTEGRATION_TASKS_ORDER[item2.rosterType] || 100));

        return statsData;
    }

    private groupByAndSumAggregatedValidationInfo(data: AggregatedValidationInfoModel[], groupByAttr: string): GroupedAggregatedValidationInfo {
        return data.reduce((prevValue: GroupedAggregatedValidationInfo, item: AggregatedValidationInfoModel) => {
            prevValue[item[groupByAttr]] = (prevValue[item[groupByAttr]] || new AggregatedValidationData());
            prevValue[item[groupByAttr]].invalidEntitiesCount += item.invalidEntitiesCount;
            prevValue[item[groupByAttr]].skippedEntitiesCount += item.skippedEntitiesCount;
            return prevValue;
        }, new GroupedAggregatedValidationInfo());
    }

    getInvalidEntitiesStats(integrationId: string = null, appGuid: string = null): Observable<AggregatedValidationInfoModel[]> {
        const integrationIdUrl = integrationId ? `${integrationId}/` : '';
        const appGuidUrl = appGuid ? `${appGuid}/` : '';
        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationIdUrl}validation/${appGuidUrl}stats`;

        // const mockData = [
        //     {rosterType: 'Student', integrationId: '1111111', invalidEntitiesCount: 100, skippedEntitiesCount: 10},
        //     {rosterType: 'School', integrationId: '1111111', invalidEntitiesCount: 200, skippedEntitiesCount: 20},
        //     {rosterType: 'Student', integrationId: '2222', invalidEntitiesCount: 50, skippedEntitiesCount: 5},
        //     {rosterType: 'School', integrationId: '2222', invalidEntitiesCount: 10, skippedEntitiesCount: 1},
        // ];
        return this.http.get<AggregatedValidationInfoModel[]>(url)
            // return Observable.of(mockData)
            .pipe(catchError(this.handleError));
    }

    getValidationStatsForIntegration(integration: Integration, schoolId: string = null): Observable<GroupedValidationRuleWithStat[]> {
        const edutoneDS: string = (integration.source.dataSourceType === DataSourceType.EduTone) ? 'source' : 'target';
        return this.getAggregatedViolatedRules(integration.integrationId, schoolId)
            .pipe(
                map((data: ValidationRuleWithStatModel[]) =>
                    data.map((stat: ValidationRuleWithStatModel) => ({
                        ...stat,
                        predicateTitle: this.getFilterPredicateTitle(stat.predicateName),
                        predicateTitleShort: this.getFilterPredicateTitleShort(stat.predicateName)
                    }))),
                // data.forEach((stat: ValidationRuleWithStatModel) => {
                //     stat.predicateTitle = this.getFilterPredicateTitle(stat.predicateName);
                //     stat.predicateTitleShort = this.getFilterPredicateTitle(stat.predicateName);
                // }))
                map((data: ValidationRuleWithStatModel[]) => this.getGroupedByRosterTypeValidationRuleWithStat(data)),
                map((groupedStats: GroupedValidationRuleWithStat[]) =>
                    groupedStats.map((groupedStatsItem: GroupedValidationRuleWithStat) => {
                        const currentTasks: IntegrationTask[] = integration.tasks
                            .filter((task: IntegrationTask) => groupedStatsItem.key === task[edutoneDS + 'Type']);
                        const attributesList = currentTasks.length > 0 ? currentTasks[0][edutoneDS + 'AttributeList'] : [];

                        groupedStatsItem.values = groupedStatsItem.values
                            .map((statItem: ValidationRuleWithStatModel) => {
                                const currentAttr: DataAttribute = attributesList
                                    .find((attr: DataAttribute) => statItem.propertyName === attr.propertyName);
                                let isMapped: boolean = currentTasks
                                    .filter((task: IntegrationTask) =>
                                        task.mappings.find((mapping: IntegrationTaskMap) =>
                                            mapping[edutoneDS + 'PropertyName'] === statItem.propertyName)
                                    ).map((task: IntegrationTask) =>
                                        task.mappings.find((mapping: IntegrationTaskMap) =>
                                            mapping[edutoneDS + 'PropertyName'] === statItem.propertyName)
                                    ).map((mapping: IntegrationTaskMap) =>
                                        mapping.enabled
                                    ).reduce((previous, current) => current || previous, false);
                                if (!isMapped && TASK_EXTRA_ATTRIBUTES[groupedStatsItem.key]) {
                                    isMapped = TASK_EXTRA_ATTRIBUTES[groupedStatsItem.key]
                                        .some((attr: ExtraAttribute) => attr.propertyName === statItem.propertyName);
                                }
                                statItem.isMapped = isMapped;
                                statItem.friendlyName = currentAttr ? currentAttr.friendlyName : statItem.propertyName;
                                return statItem;
                            });

                        groupedStatsItem.values.sort((statItem1: ValidationRuleWithStatModel, statItem2: ValidationRuleWithStatModel) =>
                            // statItem1.friendlyName.toLowerCase().localeCompare(statItem2.friendlyName.toLowerCase())
                            statItem2.invalidEntitiesCount - statItem1.invalidEntitiesCount
                        );

                        return groupedStatsItem;
                    })
                )
            );
    }

    getValidationStats(): Observable<ValidationRuleWithStatByIntegrationMode[]> {
        return this.getAggregatedViolatedRules()
            .pipe(
                tap((data: ValidationRuleWithStatModel[]) => {
                    data.forEach((stat: ValidationRuleWithStatModel) => {
                        stat.predicateTitle = this.getFilterPredicateTitle(stat.predicateName);
                        stat.friendlyName = stat.friendlyName ? stat.friendlyName : stat.propertyName;
                    });
                }),
                map((data: ValidationRuleWithStatModel[]) => this.getValidationRuleWithStatByIntegrationMode(data))
            );
    }

    getAggregatedViolatedRules(integrationId: string = null,
        schoolId: string = null, action: FilterAction = null): Observable<ValidationRuleWithStatModel[]> {
        const integrationIdUrl = integrationId ? `${integrationId}/` : '';
        const params = this.getQueryParams({
            schoolId: schoolId,
            action: action ? action.toString() : null
        });
        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationIdUrl}validation/rules?${params}`;

        return this.http.get<ValidationRuleWithStatModel[]>(url)
            .pipe(
                map((validationRulesWithStat: ValidationRuleWithStatModel[]) =>
                    validationRulesWithStat.filter((validationRuleWithStat: ValidationRuleWithStatModel) =>
                        validationRuleWithStat.invalidEntitiesCount > 0)),
                // return Observable.of(mockData)
                catchError(this.handleError)
            );
    }

    getGroupedByRosterTypeValidationRuleWithStat(data: ValidationRuleWithStatModel[]): GroupedValidationRuleWithStat[] {
        const rulesByRosterType = this.groupBy(data, 'rosterType');
        const groupedReports: GroupedValidationRuleWithStat[] = Object.keys(rulesByRosterType)
            .map(key =>
                new GroupedValidationRuleWithStat(
                    key,
                    rulesByRosterType[key]
                        .map((validationRule: ValidationRuleWithStatModel) => {
                            const sameValidationRules = rulesByRosterType[key].filter((vrItem: ValidationRuleWithStatModel) =>
                                vrItem.propertyName === validationRule.propertyName
                                && vrItem.predicateName === validationRule.predicateName
                            );

                            validationRule.skippedEntitiesCount = (validationRule.action === FilterAction.VALIDATE_AND_SKIP)
                                ? validationRule.invalidEntitiesCount
                                : 0;

                            // Check if it is the first same rule
                            if (sameValidationRules.every((vrItem: ValidationRuleWithStatModel) =>
                                !vrItem.sameRules || vrItem.sameRules.length === 0)) {

                                sameValidationRules.forEach((vrItem: ValidationRuleWithStatModel) => {
                                    if (validationRule.filterId !== vrItem.filterId) {
                                        validationRule.invalidEntitiesCount += vrItem.invalidEntitiesCount;

                                        if (vrItem.action === FilterAction.VALIDATE_AND_SKIP) {
                                            validationRule.skippedEntitiesCount =
                                                (validationRule.skippedEntitiesCount || 0) + vrItem.invalidEntitiesCount;
                                        }
                                    }

                                    validationRule.sameRules = validationRule.sameRules ? validationRule.sameRules : [];

                                    const sameRule = validationRule.sameRules.find((sameVrItem: ValidationRuleWithStatModel) =>
                                        sameVrItem.integrationId === vrItem.integrationId
                                    );
                                    if (!sameRule) {
                                        validationRule.sameRules.push(vrItem);
                                    }
                                });
                            }

                            return validationRule;
                        })
                        .filter((vrItem: ValidationRuleWithStatModel) => vrItem.sameRules && vrItem.sameRules.length > 0)
                        .sort((statItem1: ValidationRuleWithStatModel, statItem2: ValidationRuleWithStatModel) =>
                            (statItem2.invalidEntitiesCount - statItem1.invalidEntitiesCount) === 0
                                ? statItem1.propertyName.toLowerCase().localeCompare(statItem2.propertyName.toLowerCase())
                                : statItem2.invalidEntitiesCount - statItem1.invalidEntitiesCount
                        )
                )
            );

        groupedReports.sort((rosterReport1: GroupedValidationRuleWithStat, rosterReport2: GroupedValidationRuleWithStat) =>
            (INTEGRATION_TASKS_ORDER[rosterReport1.key] || 100) - (INTEGRATION_TASKS_ORDER[rosterReport2.key] || 100));

        return groupedReports;
    }

    getValidationRuleWithStatByIntegrationMode(data: ValidationRuleWithStatModel[]): ValidationRuleWithStatByIntegrationMode[] {
        const groupedStatsByIntegrationMode = this.groupBy(data, 'integrationMode');

        const result = <ValidationRuleWithStatByIntegrationMode[]>[];
        Object.keys(groupedStatsByIntegrationMode).forEach(key => {
            result.push(new ValidationRuleWithStatByIntegrationMode(
                <IntegrationMode>key,
                this.getGroupedByRosterTypeValidationRuleWithStat(groupedStatsByIntegrationMode[key])
            ));
        });
        return result;
    }

    getIntegrationsWithoutCompletedDataAccesses(appId: string): Observable<any[]> {
        const url = `${environment.datahubBaseURI}/api/v2/rosterAccess/integrations-without-access-complete/${appId}`;

        return this.http.get<any[]>(url)
            .pipe(
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    getIgnoredEntityList(integrationId: string, rosterType: string, showStage: boolean = true): Observable<string[]> {
        const params = this.getQueryParams({
            integrationId: integrationId,
            rosterType: rosterType,
            showStage: showStage
        });

        const url = `${environment.datahubBaseURI}/api/v2/duplicates/ignored?${params}`;

        return this.http.get<string[]>(url)
            .pipe(catchError(this.handleError));
    }

    getDuplicatesMetaData(metadataConfig?: DuplicatesMetadataConfig): Observable<DuplicatesMetadata> {
        return this.authenticatedUserService.authenticatedUser$.pipe(
            filter((user: User) => !!user && !!user.orgGuid))
            .pipe(
                mergeMap((user: User) => {
                    const params = this.getQueryParams({
                        dname: metadataConfig.dataSource,
                        rosterType: metadataConfig.rosterType,
                        sessionRef: metadataConfig.sessionRef,
                        searchChunk: metadataConfig.searchChunk,
                        subOrgGuid: metadataConfig.subOrgGuid,
                        organizationGuid: metadataConfig.organizationGuid,
                        status: metadataConfig.status,
                        showStage: metadataConfig.showStage ? 'true' : null
                    });

                    const url = `${environment.datahubBaseURI}/api/v2/duplicates/metadata?${params}`;

                    return this.http.get<any>(url)
                        .pipe(catchError(this.handleError));
                })
            );
    }

    getDuplicatesList(searchConfig: DuplicatesSearchConfig): Observable<DuplicateModel[]> {
        return this.authenticatedUserService.authenticatedUser$.pipe(
            filter((user: User) => !!user && !!user.orgGuid))
            .pipe(
                mergeMap((user: User) => {
                    const params = this.getQueryParams({
                        dname: searchConfig.dataSource,
                        rosterType: searchConfig.rosterType,
                        sessionRef: searchConfig.sessionRef,
                        subOrgGuid: (searchConfig.subOrgGuid) ? searchConfig.subOrgGuid : '',
                        lastIdentifier: searchConfig.lastIdentifier,
                        rowCount: searchConfig.rowCount,
                        direction: searchConfig.direction,
                        status: searchConfig.status,
                        searchChunk: (searchConfig.searchChunk) ? encodeURIComponent(searchConfig.searchChunk) : '',
                        showStage: searchConfig.showStage ? 'true' : null,
                        organizationGuid: searchConfig?.organizationGuid || ''
                    });

                    const url = `${environment.datahubBaseURI}/api/v2/duplicates/metadata/info?${params}`;

                    return this.http.get<any>(url)
                        .pipe(catchError(this.handleError));
                })
            );
    }

    getDuplicate(duplicateIssueId: string, showStage: boolean = false): Observable<DuplicateModel> {
        const queryParams = (showStage ? '?showStage=true' : '');
        const url = `${environment.datahubBaseURI}/api/v2/duplicates/metadata/info/${duplicateIssueId}${queryParams}`;

        return this.http.get<any>(url)
            .pipe(catchError(this.handleError));
    }

    checkIsDuplicate(entityGuid: string, showStage: boolean = false): Observable<string> {
        const queryParams = (showStage ? '?showStage=true' : '');
        const url = `${environment.datahubBaseURI}/api/v2/duplicates/firstopenissuewithentity/${entityGuid}${queryParams}`;

        return this.http.get(url, {responseType: 'text' as const})
            .pipe(catchError((error: HttpErrorResponse) => {
                if (error.status === 404 && error.error == 'Issue not found') {
                    return throwError('Entity not found.');
                } else {
                    return this.handleError(error);
                }
            }));
    }

    createDuplicateAsNew(
        duplicateIssueId: string,
        entityGuid: string,
        sourceEntity: SourceEntityModel
    ): Observable<SourceEntityModel> {
        const url = `${environment.datahubBaseURI}/api/v2/duplicates/metadata/info/${duplicateIssueId}/${entityGuid}`;
        const body = {
            properties: sourceEntity.properties,
            linkedGuidsMetadata: sourceEntity.linkedGuidsMetadata,
            issueEntitiesSysIds: this.convertMapToObject(sourceEntity.issueEntitiesSysIds)
        };

        return this.http.post<SourceEntityModel>(url, body)
            .pipe(catchError(this.handleError));
    }

    saveDuplicate(duplicateIssueId: string, entityGuid: string, sourceEntity: SourceEntityModel): Observable<SourceEntityModel> {
        const url = `${environment.datahubBaseURI}/api/v2/duplicates/metadata/info/${duplicateIssueId}/merge/${entityGuid}`;
        const body = {
            properties: sourceEntity.properties,
            linkedGuidsMetadata: sourceEntity.linkedGuidsMetadata,
            issueEntitiesSysIds: this.convertMapToObject(sourceEntity.issueEntitiesSysIds)
        };

        return this.http.put<SourceEntityModel>(url, body)
            .pipe(catchError(this.handleError));
    }

    saveDuplicateUser(
        duplicateIssueId: string,
        sourceEntity: SourceEntityModel
    ): Observable<SourceEntityModel> {
        const url = `${environment.datahubBaseURI}/api/v2/duplicates/metadata/info/${duplicateIssueId}/save_user`;
        const body = {
            properties: sourceEntity.properties,
            linkedGuidsMetadata: sourceEntity.linkedGuidsMetadata,
            issueEntitiesSysIds: this.convertMapToObject(sourceEntity.issueEntitiesSysIds)
        };

        return this.http.post<SourceEntityModel>(url, body)
            .pipe(catchError(this.handleError));
    }

    ignoreDuplicate(duplicateIssueId: string, entityGuid: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/duplicates/metadata/info/${duplicateIssueId}/actions/IGNORE/${entityGuid}`;

        return this.http.put<any>(url, null)
            .pipe(catchError(this.handleError));
    }

    unIgnoreDuplicate(entityGuid: string, entityType: string, integrationId: string): Observable<string> {
        const url = `${environment.datahubBaseURI}/api/v2/duplicates/unignore/${entityType}/${entityGuid}?integrationId=${integrationId}`;

        return this.http.post(url, null, {responseType: 'text' as const})
            .pipe(catchError(this.handleError));
    }

    notDuplicate(duplicateIssueId: string, entityGuid: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/duplicates/metadata/info/${duplicateIssueId}/actions/NOT_DUPLICATE/${entityGuid}`;

        return this.http.put<any>(url, null)
            .pipe(catchError(this.handleError));
    }

    deleteDuplicate(duplicateIssueId: string, entityGuid: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/duplicates/metadata/info/${duplicateIssueId}/actions/DELETE/${entityGuid}`;

        return this.http.put<any>(url, null)
            .pipe(catchError(this.handleError));
    }

    setApplicationsDataForValidationRuleWithStatByIntegrationMode(
        statsByIntegrationMode: ValidationRuleWithStatByIntegrationMode[],
        applications: ApplicationProviderListModel[]
    ): ValidationRuleWithStatByIntegrationMode[] {
        const appsData: Object = applications.reduce((obj: Object, app: ApplicationProviderListModel) => {
            obj[app.integrationId] = app;
            return obj;
        }, {});

        return statsByIntegrationMode.map((statByIntegrationMode: ValidationRuleWithStatByIntegrationMode) => {
            statByIntegrationMode.groupedStat = statByIntegrationMode.groupedStat
                .map((groupedStat: GroupedValidationRuleWithStat) => {
                    groupedStat.values = groupedStat.values.map((validationRule: ValidationRuleWithStatModel) => ({
                        ...validationRule,
                        applicationName: appsData[validationRule.integrationId] ? appsData[validationRule.integrationId].name : '',
                        applicationGuid: appsData[validationRule.integrationId] ? appsData[validationRule.integrationId].guid : '',
                        applicationIcon: appsData[validationRule.integrationId] ? appsData[validationRule.integrationId].icon : '',
                        sameRules: validationRule.sameRules.map((sameValidationRule: ValidationRuleWithStatModel) => ({
                            ...sameValidationRule,
                            applicationName:
                                    appsData[sameValidationRule.integrationId] ? appsData[sameValidationRule.integrationId].name : '',
                            applicationGuid:
                                    appsData[sameValidationRule.integrationId] ? appsData[sameValidationRule.integrationId].guid : '',
                            applicationIcon:
                                    appsData[sameValidationRule.integrationId] ? appsData[sameValidationRule.integrationId].icon : ''
                        })).sort((a: ValidationRuleWithStatModel, b: ValidationRuleWithStatModel) =>
                            a.applicationName.toLowerCase().localeCompare(b.applicationName.toLowerCase())
                        )
                    }));
                    return groupedStat;
                });
            return statByIntegrationMode;
        });
    }

    getInvalidEntities(integrationId: string, rosterType: string, action: FilterAction = null,
        schoolId: string = null, filter: string = null,
        lastGuid: string = null, rowCount: number = 50): Observable<InvalidEntityModel[]> {
        const params = this.getQueryParams({
            action: action ? action.toString() : null,
            schoolId: schoolId,
            filter: filter,
            lastGuid: lastGuid,
            rowCount: rowCount.toString()
        });
        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/validation/invalid/${rosterType}?${params}`;

        return this.http.get<InvalidEntityModel[]>(url)
            .pipe(catchError(this.handleError));
    }

    downloadInvalidEntities(integrationId: string, rosterType: string, action: FilterAction = null,
        schoolId: string = null, filter: string = null) {
        const headers = new HttpHeaders();
        headers.append('Content-Type', 'application/csv');
        headers.append('Accept', 'application/csv');
        const params = this.getQueryParams({
            action: action ? action.toString() : null,
            schoolId: schoolId,
            filter: filter
        });
        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/validation/invalid/${rosterType}/download?${params}`;

        return this.http.get(url, {
            headers: headers,
            responseType: 'blob' as const
        })
            .subscribe((data) => {
                const blob = new Blob([data], {type: 'application/csv'});
                const date = new Date();

                const downloadUrl = window.URL.createObjectURL(blob);
                const downloadInvalidEntitiesLink = document.createElement('a');
                document.body.appendChild(downloadInvalidEntitiesLink);
                downloadInvalidEntitiesLink.setAttribute('style', 'display: none');
                downloadInvalidEntitiesLink.href = window.URL.createObjectURL(blob);
                downloadInvalidEntitiesLink.download = `${rosterType}_${date.getUTCFullYear()}_${date.getUTCMonth()}_${date.getUTCDay()}_${date.getUTCHours()}_${date.getUTCMinutes()}.csv`;
                downloadInvalidEntitiesLink.click();
                window.URL.revokeObjectURL(downloadUrl);
                downloadInvalidEntitiesLink.remove(); // remove the element
            },
            (error) => console.log('Could not load data quality details.'));
    }

    getInvalidEntitiesByFilterId(integrationId: string, filterId: string,
        schoolId: string = null, filter: string = null,
        lastGuid: string = null, rowCount: number = 50): Observable<InvalidEntityModel[]> {
        const params = this.getQueryParams({
            filterId: filterId,
            schoolId: schoolId,
            filter: filter,
            lastGuid: lastGuid,
            rowCount: rowCount.toString()
        });
        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/validation/invalid?${params}`;

        return this.http.get<InvalidEntityModel[]>(url)
            .pipe(catchError(this.handleError));
    }

    downloadInvalidEntitiesByFilterId(integrationId: string, filterId: string,
        schoolId: string = null, filter: string = null) {
        const headers = new HttpHeaders();
        headers.append('Content-Type', 'application/csv');
        headers.append('Accept', 'application/csv');
        const params = this.getQueryParams({
            filterId: filterId,
            schoolId: schoolId,
            filter: filter
        });
        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/validation/invalid/download?${params}`;

        return this.http.get(url, {
            headers: headers,
            responseType: 'blob' as const
        })
            .subscribe((data) => {
                const blob = new Blob([data], {type: 'application/csv'});
                const date = new Date();
                const defaultFileName = `${filterId}_${date.getUTCFullYear()}_${date.getUTCMonth() + 1}_${date.getUTCDate()}_${date.getUTCHours()}_${date.getUTCMinutes()}.csv`;

                if ((<any>window.navigator).msSaveOrOpenBlob) {  // IE & Edge
                    (<any>window.navigator).msSaveBlob(blob, defaultFileName);
                } else {
                    const downloadUrl = window.URL.createObjectURL(blob);
                    const downloadInvalidEntitiesLink = document.createElement('a');
                    document.body.appendChild(downloadInvalidEntitiesLink);
                    downloadInvalidEntitiesLink.setAttribute('style', 'display: none');
                    downloadInvalidEntitiesLink.href = downloadUrl;
                    downloadInvalidEntitiesLink.download = defaultFileName;
                    downloadInvalidEntitiesLink.click();
                    window.URL.revokeObjectURL(downloadUrl);
                    downloadInvalidEntitiesLink.remove(); // remove the element
                }
            },
            (error) => console.log('Could not load data quality details.'));
    }

    downloadHistoryFiles(sessionId: number) {
        const headers = new HttpHeaders();

        const url = `${environment.datahubBaseURI}/api/v2/applications/sftp/uploadfiles/sessions/${sessionId}/files`;

        return this.http.get(url, {
            headers: headers,
            responseType: 'blob' as const
        })
            .subscribe((data) => {
                const blob = new Blob([data], {type: 'application/zip'});

                const downloadUrl = window.URL.createObjectURL(blob);
                const downloadHistoryFilesLink = document.createElement('a');
                document.body.appendChild(downloadHistoryFilesLink);
                downloadHistoryFilesLink.setAttribute('style', 'display: none');
                downloadHistoryFilesLink.href = window.URL.createObjectURL(blob);
                downloadHistoryFilesLink.download = `files_${sessionId}.zip`;
                downloadHistoryFilesLink.click();
                window.URL.revokeObjectURL(downloadUrl);
                downloadHistoryFilesLink.remove(); // remove the element
            },
            (error) => console.log('Could not load files.'));
    }

    downloadHistoryShortcuts(syncHistoryItem: SyncHistoryItem, statId: number, rosterType: string,
                             partition: string = null, direction: DataStatisticDirection = null,
                             operation: ShortcutOperation | ShortcutOperation[] = null, filter: string = null) {
        const headers = {
            'Content-Type': 'application/csv',
            'Accept': 'application/csv'
        };
        const params = {
            rosterType: rosterType,
            operation: Array.isArray(operation) ? operation.join(',') : operation,
            partition: partition ? encodeURIComponent(partition) : partition,
            direction: direction,
            filter: filter
        };

        Object.keys(params).forEach((paramName: string) => {
            if (params[paramName] === null) {
                delete params[paramName];
            }
        });

        rosterType = (operation === null ? 'total' : rosterType);
        let result$;
        let url = `${environment.datahubBaseURI}/api/v2/applications/`;
        const suffix = `/executions/${statId}/shortcuts/${rosterType}/download`;
        const options = {
            headers: headers,
            responseType: 'blob' as const,
            params: params
        };

        if (syncHistoryItem.sharedPoolGuid) {
            url += `shared_pool/${syncHistoryItem.sharedPoolGuid}` + suffix;
            options['headers']['Content-Type'] = 'multipart/form-data; boundary=WebAppBoundary';
            result$ = this.http.post(url, null, options);
        } else {
            url += `integrations/${syncHistoryItem.integrationId}` + suffix;
            result$ = this.http.get(url, options);
        }

        return result$.subscribe((data) => {
                const blob = new Blob([data], {type: 'application/csv'});
                const date = new Date();
                const defaultFileName = `${statId}_${date.getUTCFullYear()}_${date.getUTCMonth() + 1}_${date.getUTCDate()}_${date.getUTCHours()}_${date.getUTCMinutes()}.csv`;

                if ((<any>window.navigator).msSaveOrOpenBlob) {  // IE & Edge
                    (<any>window.navigator).msSaveBlob(blob, defaultFileName);
                } else {
                    const downloadUrl = window.URL.createObjectURL(blob);
                    const downloadInvalidEntitiesLink = document.createElement('a');
                    document.body.appendChild(downloadInvalidEntitiesLink);
                    downloadInvalidEntitiesLink.setAttribute('style', 'display: none');
                    downloadInvalidEntitiesLink.href = downloadUrl;
                    downloadInvalidEntitiesLink.download = defaultFileName;
                    downloadInvalidEntitiesLink.click();
                    window.URL.revokeObjectURL(downloadUrl);
                    downloadInvalidEntitiesLink.remove(); // remove the element
                }
            },
            (error) => console.log('Could not load CSV file.')
            );
    }

    downloadHistoryErrorsShortcuts(integrationId: string, statId: number, schoolName: string = null, fileName: string = null,
        errorType: HistoryErrorType = null, message: string = null) {
        const headers = new HttpHeaders();
        headers.append('Content-Type', 'application/csv');
        headers.append('Accept', 'application/csv');
        const params = this.getQueryParams({
            schoolName: schoolName,
            fileName: fileName,
            errorType: errorType,
            message: message
        });

        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}/executions/${statId}/shortcuts/errors/download?${params}`;

        return this.http.get(url, {
            headers: headers,
            responseType: 'blob' as const
        })
            .subscribe((data) => {
                const blob = new Blob([data], {type: 'application/csv'});
                const date = new Date();
                const defaultFileName = `${fileName ? fileName + '_' + statId : statId}_${date.getUTCFullYear()}_${date.getUTCMonth() + 1}_${date.getUTCDate()}_${date.getUTCHours()}_${date.getUTCMinutes()}.csv`;

                if ((<any>window.navigator).msSaveOrOpenBlob) {  // IE & Edge
                    (<any>window.navigator).msSaveBlob(blob, defaultFileName);
                } else {
                    const downloadUrl = window.URL.createObjectURL(blob);
                    const downloadInvalidEntitiesLink = document.createElement('a');
                    document.body.appendChild(downloadInvalidEntitiesLink);
                    downloadInvalidEntitiesLink.setAttribute('style', 'display: none');
                    downloadInvalidEntitiesLink.href = downloadUrl;
                    downloadInvalidEntitiesLink.download = defaultFileName;
                    downloadInvalidEntitiesLink.click();
                    window.URL.revokeObjectURL(downloadUrl);
                    downloadInvalidEntitiesLink.remove(); // remove the element
                }
            },
            (error) => console.log('Could not load CSV file.'));
    }

    getOnboardingWizardCompletedSteps(orgGuid: string): Observable<string[]> {
        const url = `${environment.datahubBaseURI}/api/v2/audit/onboarding/organizations/${orgGuid}/steps`;

        return this.http.get<string[]>(url)
            .pipe(catchError(this.handleError));
    }

    getOnboardingWizardSteps(completedSteps: string[]): OnboardingWizardStep[] {
        return ProviderOnboardingWizard.map((item: OnboardingWizardStep) => {
            item.completed = (completedSteps || []).includes(<string>item.step);
            return item;
        });
    }

    // Add Identity Provider (IdP)
    // View merging rules
    // Enable MFA for any & all age of user
    accomplishOnboardingWizardStep(orgGuid: string, step: OnboardingWizardStepNames): any {
        const url = `${environment.datahubBaseURI}/api/v2/audit/onboarding/organizations/${orgGuid}/steps`;
        const params = {stepName: <string>step};

        return this.http.post(url, null, {params: params})
            .pipe(catchError(this.handleError));
    }

    getFilterPredicateTitle(predicateName: FilterPredicate): string {
        return this.PREDICATE_TITLES[predicateName] ? this.PREDICATE_TITLES[predicateName] : predicateName;
    }

    getFilterPredicateTitleShort(predicateName: FilterPredicate): string {
        return this.PREDICATE_TITLES_SHORT[predicateName] ? this.PREDICATE_TITLES_SHORT[predicateName] : predicateName;
    }

    groupDataAttributesByType(attributes: DataAttribute[]): GroupedDataAttribute {
        return <GroupedDataAttribute>this.groupBy(attributes, 'propertyTypeDenom');
    }

    public getAvailableFiltredMappingAttributes(
        attributeList: DataAttribute[],
        attributesByType: GroupedDataAttribute,
        selectedProperty: DataAttribute,
        filtredValue?: string
    ): DataAttribute[] {

        const attributesList = this.getTaskAllowedMappingAttributes(
            attributeList,
            attributesByType,
            selectedProperty
        );
        return filtredValue
            ? this.filterAttribute(attributeList, filtredValue)
            : attributesList
                .sort((attr1: DataAttribute, attr2: DataAttribute) =>
                    (attr1.friendlyName || attr1.propertyName).toLowerCase()
                        .localeCompare((attr2.friendlyName || attr2.propertyName).toLowerCase()
                        ));
    }

    getTaskAllowedMappingAttributes(
        attributeList: DataAttribute[],
        attributesByType: GroupedDataAttribute,
        selectedProperty: DataAttribute
    ): DataAttribute[] {
        // Type validation is disabled until attributes will have correct types
        // if (selectedProperty && selectedProperty.propertyTypeDenom) {
        //     return (attributesByType[selectedProperty.propertyTypeDenom]
        //         ? attributesByType[selectedProperty.propertyTypeDenom]
        //         : []);
        // } else {
        //     return attributeList;
        // }
        if (selectedProperty && selectedProperty.isExtensionField && selectedProperty.propertyTypeDenom) {
            return (attributesByType[selectedProperty.propertyTypeDenom]
                ? attributesByType[selectedProperty.propertyTypeDenom]
                : []);
        } else {
            if (selectedProperty && selectedProperty.propertyTypeDenom) {
                const extensionAttrs = attributeList.filter((attr: DataAttribute) => attr.isExtensionField);
                if (extensionAttrs.length > 0) {
                    return attributeList.filter((attr: DataAttribute) =>
                        !attr.isExtensionField || (attr.propertyTypeDenom === selectedProperty.propertyTypeDenom)
                    );
                } else {
                    return attributeList;
                }
            } else {
                return attributeList;
            }
        }
    }

    private filterAttribute(attributeList: DataAttribute[], filtredValue: string): DataAttribute[] {
        return this.sortDataAttributes(
            attributeList.filter((attribute: DataAttribute) =>
                (attribute.propertyName || attribute.friendlyName)
                && (attribute.propertyName || attribute.friendlyName).toLowerCase().indexOf(filtredValue.toLowerCase()) > -1),
            filtredValue
        );
    }

    private sortDataAttributes(filteredDataAttributes: DataAttribute[], filterValue = ''): DataAttribute[] {
        return filteredDataAttributes.sort((attrA: DataAttribute, attrB: DataAttribute) => {
            if (((attrA.propertyName).toLowerCase().trim().indexOf(filterValue)) === 0
                || ((attrB.propertyName).toLowerCase().trim().indexOf(filterValue)) === 0) {
                if (((attrA.propertyName).toLowerCase().trim().indexOf(filterValue))
                    > ((attrB.propertyName).toLowerCase().trim().indexOf(filterValue))) {
                    return 1;
                }
                if (((attrA.propertyName).toLowerCase().trim().indexOf(filterValue))
                    < ((attrB.propertyName).toLowerCase().trim().indexOf(filterValue))) {
                    return -1;
                }
            }
            if ((attrA.propertyName).toLowerCase().trim() > (attrB.propertyName).toLowerCase().trim()) {
                return 1;
            }
            if ((attrA.propertyName).toLowerCase().trim() < (attrB.propertyName).toLowerCase().trim()) {
                return -1;
            }
            return 0;
        });
    }

    public filterMappedTargetAttributes(
        targetAttributeList: DataAttribute[],
        mappings: IntegrationTaskMap[],
        mapping: IntegrationTaskMap = null,
        isSyncMode: boolean = false
    ): DataAttribute[] {

        const mappedAttributesTargetPropertyNameList = this.getMappedAttributesTargetPropertyNameList(mappings, isSyncMode, mapping);

        return targetAttributeList.filter(attribute => {
            if (!mappedAttributesTargetPropertyNameList.includes(attribute.propertyName)) {
                return attribute;
            }
        });
    }

    private getMappedAttributesTargetPropertyNameList(
        mappings: IntegrationTaskMap[],
        isSyncMode: boolean,
        mapping: IntegrationTaskMap
    ) {
        return !mapping
            ? []
            : mappings.map((mappingItem: IntegrationTaskMap) => {
                // current attribute mapping item
                if ((mappingItem.targetPropertyName !== mapping.targetPropertyName)
                    && (!isSyncMode
                        || (isSyncMode && (mapping.direction === AttributeMappingDirection.SYNC)))) {
                    return mappingItem.targetPropertyName;
                }
            });
    }

    public getAvailableOrganizations(
        organizations: OrgItem[], filtredOrgName: string, notAllowedOrganizationsObj: {}, objKeyName: 'name' | 'identifier' = 'name'
    ): OrgItem[] {
        return filtredOrgName
            ? this.filterOrganizations(organizations, filtredOrgName, notAllowedOrganizationsObj, objKeyName)
            : (
                Object.keys(notAllowedOrganizationsObj).length > 0
                    ? organizations.filter((org: OrgItem) => !notAllowedOrganizationsObj[org[objKeyName]])
                    : organizations
            );
    }

    private filterOrganizations(
        organizations: OrgItem[], filterValue: string, notAllowedOrganizationsObj: {}, objKeyName: 'name' | 'identifier' = 'name'
    ): OrgItem[] {
        return organizations
            .filter((org: OrgItem) =>
                (org.name.toLowerCase().trim().indexOf(filterValue) > -1)
                && !notAllowedOrganizationsObj[org[objKeyName]])
            .sort((orgA: OrgItem, orgB: OrgItem) => {
                if (((orgA.name).toLowerCase().trim().indexOf(filterValue)) === 0
                    || ((orgB.name).toLowerCase().trim().indexOf(filterValue)) === 0) {
                    if (((orgA.name).toLowerCase().trim().indexOf(filterValue)) > ((orgB.name).toLowerCase().trim().indexOf(filterValue))) {
                        return 1;
                    }
                    if (((orgA.name).toLowerCase().trim().indexOf(filterValue)) < ((orgB.name).toLowerCase().trim().indexOf(filterValue))) {
                        return -1;
                    }
                }
                if ((orgA.name).toLowerCase().trim() > (orgB.name).toLowerCase().trim()) {
                    return 1;
                }
                if ((orgA.name).toLowerCase().trim() < (orgB.name).toLowerCase().trim()) {
                    return -1;
                }
                return 0;
            });
    }

    groupBy(xs, key) {
        return xs.reduce((rv, x) => {
            (rv[x[key]] = rv[x[key]] || []).push(x);
            return rv;
        }, {});
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            console.error('An error occurred:', error.error.message);
        } else {
            console.error(
                `Backend returned code ${error.status}, ` +
                `body was: ${error.message}`);
        }
        return throwError('Something bad happened, please try again later.');
    }

    private convertMapToObject(mapInstance): Object {
        const obj = {};
        for (const prop of mapInstance) {
            obj[prop[0]] = prop[1];
        }
        return obj;
    }

    private getQueryParams(params: Object): string {
        return Object.keys(params)
            .filter((key: string) => params[key])
            .map((key: string) => `${key}=${params[key]}`)
            .join('&');
    }
}

